/* T16 search.
   <predictive-search>: the header search box. From 2 letters it fetches sections/predictive-search.liquid
   through Shopify's predictive search (products, articles, pages and query suggestions) and shows it under
   the box; before that, the popular links. Enter goes to /search. Esc closes the panel. The /search page's own
   box uses the same element (data-dismiss), with the results as a panel over the page.
   <search-tabs>: the /search page tabs (All, Robots and parts, Support, Guides) and "See all" buttons. It is the one
   results page for every search box (header, support centre, robot support pages):
   - ?tab=support (or products, guides) opens that tab.
   - ?robot=<handle> scopes the Support results to that robot's articles plus the all-robots ones (empty Products
     field); the scope chips switch it, and the Support counts follow.
   - The searched words are marked in Support results (words of 3+ letters, minus filler words).
   - With no search answer, the Support tab shows the first visible Support result's quick answer and "Did this fix it?".
   Old support search links (/search?view=support&...) are sent to /search?...&tab=support. */

// The retired support search template: keep q and robot, open on the Support tab
(() => {
  const url = new URL(window.location.href);
  if (!/\/search\/?$/.test(url.pathname) || url.searchParams.get('view') !== 'support') return;
  url.searchParams.delete('view');
  url.searchParams.delete('type');
  url.searchParams.set('tab', 'support');
  window.location.replace(url.toString());
})();

// Search answers: [signup_offer] in an answer's lead is the footer sign-up heading
function fillSignupOffer(root) {
  const offer = document.getElementById('FooterNewsletterHeading')?.textContent.trim();
  if (offer) root.querySelectorAll('[data-signup-offer]').forEach((el) => { el.textContent = offer; });
}
document.addEventListener('DOMContentLoaded', () => fillSignupOffer(document));

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-ps-results]');
    this.popular = this.querySelector('[data-ps-popular]');
    if (!this.input || !this.results) return;
    this.url = this.dataset.url;
    this.cache = new Map();
    this.timer = null;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 180);
    });
    // data-dismiss (the /search page box): a click outside the box and its panel hides the panel
    if ('dismiss' in this.dataset) {
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim() !== '') this.results.hidden = false; });
    }
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.input.value = '';
        this.update();
        document.querySelector('[data-search-toggle][aria-expanded="true"]')?.click();
      }
    });
  }

  async update() {
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.popular) this.popular.hidden = false;
      return;
    }
    if (this.popular) this.popular.hidden = true;
    if (this.controller) this.controller.abort();
    let html = this.cache.get(q);
    if (!html) {
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'query,product,article,page',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        // Articles and pages match on their tags and text too, not only the title
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body'
      });
      try {
        const response = await fetch(`${this.url}?${params}`, { signal: this.controller.signal });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        html = doc.querySelector('#predictive-search-results')?.outerHTML || '';
        this.cache.set(q, html);
      } catch (error) {
        return;
      }
    }
    if (this.input.value.trim() !== q) return;
    this.results.innerHTML = html;
    this.results.hidden = html === '';
    fillSignupOffer(this.results);
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

// A support search (?tab=support, from the support centre or a robot page): the T9 band's Support crumb and robot
// chips, the support topic chips instead of the site link chips when nothing is found, and the box keeps tab and
// robot for the next search. Topic chip searches carry the robot too.
(() => {
  if (!/\/search\/?$/.test(window.location.pathname)) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('tab') !== 'support') return;
  document.body.dataset.supportSearch = 'true';
  const robot = params.get('robot');
  const ready = () => {
    document.querySelectorAll('[data-support-only]').forEach((el) => { el.hidden = false; });
    document.querySelectorAll('[data-site-only]').forEach((el) => { el.hidden = true; });
    if (!document.querySelector('[data-support-item]')) document.querySelectorAll('.sr-scope').forEach((el) => { el.hidden = true; });
    document.querySelectorAll('[data-keep="tab"]').forEach((input) => { input.disabled = false; });
    if (robot) {
      document.querySelectorAll('[data-keep="robot"]').forEach((input) => { input.value = robot; input.disabled = false; });
      document.querySelectorAll('a[data-support-link]').forEach((a) => {
        const url = new URL(a.href, window.location.origin);
        if (url.pathname.replace(/\/$/, '') === '/search' && !url.searchParams.has('robot')) { url.searchParams.set('robot', robot); a.href = url.pathname + url.search; }
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
})();

class SearchTabs extends HTMLElement {
  connectedCallback() {
    this.chips = [...document.querySelectorAll('[data-scope]')];
    this.chips.forEach((chip) => chip.addEventListener('click', () => this.scope(chip.dataset.scope)));
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tab]');
      if (!button) return;
      this.show(button.dataset.tab);
      if (button.classList.contains('sr-group__all')) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.init();
  }

  init() {
    const params = new URLSearchParams(window.location.search);
    this.items = [...this.querySelectorAll('[data-support-item]')];
    this.quick = this.querySelector('[data-support-quick]');
    this.highlight(params.get('q') || '');
    const robot = params.get('robot');
    this.scope(this.chips.some((c) => c.dataset.scope === robot) ? robot : 'all');
    const tab = params.get('tab');
    this.show(tab && this.querySelector(`[role="tab"][data-tab="${CSS.escape(tab)}"]`) ? tab : this.dataset.group || 'all');
  }

  // Shopify's search across products, articles and pages returns only a few articles, so the Support and Guides
  // groups come from a second search of articles and pages (same page, &type=article,page) and replace the first ones.
  merge(doc) {
    const fresh = doc.querySelector('search-tabs');
    if (!fresh) return;
    const groups = this.querySelector('.sr-groups');
    const tabs = this.querySelector('.sr-tabs');
    const productsGroup = groups.querySelector('[data-group-key="products"]');
    const questionFirst = productsGroup && groups.firstElementChild !== productsGroup;
    ['support', 'guides'].forEach((key) => this.querySelectorAll(`[data-group-key="${key}"], .sr-tabs [data-tab="${key}"]`).forEach((el) => el.remove()));
    const newGroups = [...fresh.querySelectorAll('[data-group-key="support"], [data-group-key="guides"]')];
    if (productsGroup && !questionFirst) productsGroup.after(...newGroups);
    else groups.prepend(...newGroups);
    const productsTab = tabs.querySelector('[data-tab="products"]') || tabs.querySelector('[data-tab="all"]');
    productsTab.after(...fresh.querySelectorAll('.sr-tabs [data-tab="support"], .sr-tabs [data-tab="guides"]'));
    const num = (el) => Number((el?.textContent.match(/\d+/) || [0])[0]);
    const nProducts = num(tabs.querySelector('[data-tab="products"]'));
    const nSupport = fresh.querySelectorAll('[data-support-item]').length;
    const nGuides = num(tabs.querySelector('[data-tab="guides"]'));
    const all = this.querySelector('[data-all-count]');
    if (all) { all.dataset.base = nProducts + nGuides; all.textContent = `(${nProducts + nSupport + nGuides})`; }
    const heading = this.querySelector('.sr-heading');
    if (heading) heading.textContent = heading.textContent.replace(/^\d+/, nProducts + nSupport + nGuides);
    if (!this.quick && fresh.querySelector('[data-support-quick]')) this.querySelector('.sr-layout').before(fresh.querySelector('[data-support-quick]'));
    this.init();
  }

  show(key) {
    this.dataset.group = key;
    this.querySelectorAll('[role="tab"]').forEach((tab) => {
      const on = tab.dataset.tab === key;
      tab.classList.toggle('is-on', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.querySelectorAll('[data-group-key]').forEach((group) => {
      group.hidden = key !== 'all' && group.dataset.groupKey !== key;
    });
    this.updateQuick();
  }

  // Support results for one robot (its own articles and the all-robots ones), or all of them
  scope(key) {
    if (!this.items.length) return;
    let shown = 0;
    this.items.forEach((item) => {
      const robots = (item.dataset.robots || '').split(' ').filter(Boolean);
      const on = key === 'all' || robots.length === 0 || robots.includes(key);
      item.hidden = !on;
      if (on) {
        shown += 1;
        item.toggleAttribute('data-more', shown > 3);
      }
    });
    this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.scope === key)));
    // Breadcrumb: Home / Support / [robot] / Search on a robot-scoped support search
    const crumb = document.querySelector('[data-robot-crumb]');
    const chip = this.chips.find((c) => c.dataset.scope === key && key !== 'all');
    if (crumb) {
      crumb.hidden = !chip || document.body.dataset.supportSearch !== 'true';
      if (chip) { crumb.querySelector('a').textContent = chip.dataset.name; crumb.querySelector('a').href = chip.dataset.url; }
    }
    document.querySelectorAll('[data-keep="robot"]').forEach((input) => { input.value = key === 'all' ? '' : key; input.disabled = key === 'all' || document.body.dataset.supportSearch !== 'true'; });
    this.querySelectorAll('[data-support-count]').forEach((el) => { el.textContent = `(${shown})`; });
    const all = this.querySelector('[data-all-count]');
    if (all) all.textContent = `(${Number(all.dataset.base) + shown})`;
    this.querySelector('[data-no-scope]')?.toggleAttribute('hidden', shown > 0);
    this.updateQuick();
  }

  // No search answer: on the Support tab, the first visible Support result's quick answer
  updateQuick() {
    if (!this.quick) return;
    const first = this.items.find((item) => !item.hidden && item.querySelector('template[data-quick]'));
    const on = this.dataset.group === 'support' && !!first;
    if (on) this.quick.querySelector('[data-quick-body]').innerHTML = first.querySelector('template[data-quick]').innerHTML;
    this.quick.hidden = !on;
  }

  highlight(q) {
    const stop = new Set(['the', 'and', 'for', 'with', 'how', 'why', 'what', 'does', 'can', 'not', 'wont', "won't", 'cant', "can't", 'my', 'its', "it's", 'you', 'your', 'from', 'when', 'will', 'into', 'out']);
    const words = q.toLowerCase().split(/[^a-z0-9'µ]+/).filter((w) => w.length >= 3 && !stop.has(w));
    if (!words.length) return;
    const esc = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${words.map(esc).join('|')})`, 'gi');
    this.querySelectorAll('[data-group-key="support"] [data-highlight]').forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const parts = node.textContent.split(re);
        if (parts.length < 2) return;
        const frag = document.createDocumentFragment();
        parts.forEach((part, i) => {
          if (i % 2) { const m = document.createElement('mark'); m.textContent = part; frag.append(m); } else if (part) frag.append(part);
        });
        node.replaceWith(frag);
      });
    });
  }
}
if (!customElements.get('search-tabs')) customElements.define('search-tabs', SearchTabs);

// Fetch the articles-and-pages search once per results page and merge it in (see SearchTabs.merge)
(() => {
  if (!/\/search\/?$/.test(window.location.pathname)) return;
  const url = new URL(window.location.href);
  if (!url.searchParams.get('q') || url.searchParams.has('type')) return;
  url.searchParams.set('type', 'article,page');
  const run = async () => {
    let doc;
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      return;
    }
    const current = document.querySelector('search-tabs');
    if (current) { current.merge(doc); return; }
    // Nothing found across all types, but articles or pages were: show those results instead
    const fresh = doc.querySelector('.sr search-tabs');
    const empty = document.querySelector('.sr .sr-empty');
    if (fresh && empty) {
      // The fresh results carry their own search answer
      document.querySelectorAll('.sr > .sa').forEach((el) => el.remove());
      empty.replaceWith(fresh);
    }
  };
  if (customElements.get('search-tabs')) run(); else window.addEventListener('DOMContentLoaded', run);
})();
